import React from 'react'
import { Navigate, useLocation } from 'react-router-dom'
import { useAuth } from '../../hooks/useAuth'
import type { UserRole } from '../../types/user'

interface ProtectedRouteProps {
  children: React.ReactNode
  /** Role yang diizinkan mengakses route ini */
  allowedRoles?: UserRole[]
  /** Redirect target jika tidak terotorisasi */
  redirectTo?: string
}

/**
 * Guard route untuk halaman yang memerlukan autentikasi.
 * - Menampilkan loading spinner saat auth belum diinisialisasi
 * - Redirect ke /login jika tidak login
 * - Redirect ke /not-authorized jika role tidak sesuai
 */
export function ProtectedRoute({
  children,
  allowedRoles,
  redirectTo = '/login',
}: ProtectedRouteProps) {
  const { isInitialized, isLoading, authUser } = useAuth()
  const location = useLocation()

  // Masih memuat status auth
  if (!isInitialized || isLoading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-ivory">
        <div className="flex flex-col items-center gap-4">
          <div className="w-10 h-10 border-4 border-cyan-600 border-t-transparent rounded-full animate-spin" />
          <p className="text-slate-500 text-sm">Memuat...</p>
        </div>
      </div>
    )
  }

  // Belum login
  if (!authUser) {
    return <Navigate to={redirectTo} state={{ from: location }} replace />
  }

  // Akun tidak aktif
  if (!authUser.isActive) {
    return <Navigate to="/login?status=suspended" replace />
  }

  // Role tidak sesuai
  if (allowedRoles && !allowedRoles.includes(authUser.role!)) {
    return <Navigate to="/not-authorized" replace />
  }

  return <>{children}</>
}
